/**
 * BL-815 — `canBatchApply`: the single transition-safeguard predicate every lot
 * *batch* edit consults, keyed on the lot's ADR-023 derived phase
 * ({@link LotPhase}) × the requested {@link BatchChange}. It is the code
 * encoding of `docs/feature/admin/batch-edit-transition-matrix.md` (signed off
 * 2026-07-29, lifecycle-consent). Both the single-item and batch paths call it
 * so the rules can never drift into divergent copies (BL-815 AC 2).
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth, no stamp
 * writes (RAC 3 — it only *reads* phase), client-safe (BL-817's bulk bar uses it
 * to pre-disable ineligible actions). Sibling of `lot-openability.ts` /
 * `lot-phase-predicates.ts`.
 *
 * **No SQL-parity mirror** (unlike `lot-openability` ↔ `openableLotWhere`): batch
 * validation is per-row in the service loop, not a set-based WHERE, so there is
 * no bulk query to keep in parity — a deliberate scope decision, not an omission.
 *
 * **Financially-dangerous transitions are un-representable** — reverse-a-sale,
 * hammer-adjust, swap-buyer, and dispose are simply absent from {@link BatchChange}
 * (matrix §1/§3), so a batch physically cannot request them. They stay on the
 * single-item guarded routes / the invoice Price adjustment
 * (`LotPriceAdjustmentService`, `hammer-price-sot.md`).
 */
import type { LotPhase } from './phase-tokens';
import { isLotPhaseTerminal } from './lot-phase-predicates';

/**
 * A batch-settable change, the predicate's second axis. The union is the union
 * of what the three admin surfaces (Sequencing tab, auction Items tab, /inventory
 * list) expose — see the matrix doc §1. `set-status` carries only the two safe
 * batch targets: marking `sold`/`active` is a financial/auto event, not a bulk
 * status set (matrix §5.1).
 */
export type BatchChange =
  | { type: 'set-status'; target: 'draft' | 'ready' }
  // BL-1927 retired `reassign-ring`: the Ordering tab's bulk Ring answers from
  // the lot panel's rule book (`evaluateLotFieldEditability(...).ringId`, via
  // `ringVerdictForBatch`) with the caller's permission — not from this matrix.
  | { type: 'group' }
  | { type: 'ungroup' }
  | { type: 'delete' }
  | { type: 'assign-to-auction' }
  // BL-1910 (M2) retired reassign-seller / set-category / set-location /
  // toggle-titled: Edit lots' `edit-fields` answers per field from the lot
  // panel's rule book (`evaluateLotFieldEditability`), not from this matrix.
  // BL-1073 lifted TWO verdicts out of admin-web's batch adapter so no bulk
  // action carried an eligibility rule the shared core didn't own (ADR 034 AC 1).
  // BL-1431 collapsed the two into ONE: `delete` and `remove-from-auction` were
  // two names for the same column (`auction_lots.deleted_at`) and the same write,
  // disagreeing on the bid-bearing pre-open cell. `delete` is the survivor — the
  // operator ruling (2026-09-01) made it the only delete a lot has, and it now
  // reaches home lots and archives the emptied inventory item, neither of which
  // "remove from auction" describes. `copy-to-auction` survives.
  //
  // COPY the lot into another auction (ADR 028 §5 — a fresh row under the same
  // `inventory_item_id`, source preserved). Deliberately DISTINCT from
  // `assign-to-auction`, which is the literal in-place assign of an
  // unassociated home lot: copying moves no bid state, so a sold lot IS
  // copyable (that is the relist path) while it is NOT assignable in place.
  | { type: 'copy-to-auction' };

/** Why a batch change was refused for a given lot (matrix §2 legend). */
export type BatchBlockReason =
  | 'terminal' // terminal-lot immutability (sold/unsold/withdrawn/deleted)
  | 'sold-lock' // a SOLD_LOCKED_FIELDS change on a sold lot
  // BL-1430 (matrix amendment M5, §2) — the SELECTION-level sold lock on the
  // bulk lifecycle axis. Deliberately NOT `sold-lock`, which is a per-lot verdict
  // ("this lot is sold, so this change can't apply to it"). This one is a
  // statement about the SELECTION: "a sold lot is in your selection, so no
  // lifecycle action applies to ANY of it — reverse those sales first, one at a
  // time." Leaving `sold` is the only terminal exit carrying financial teardown,
  // and Reverse Sale is per-lot by construction, so twelve lots cannot route
  // into one modal.
  //
  // Emitted by the batch CHECK service's selection overlay, never by a per-lot
  // rule core — no core has the selection in scope. It also OUTRANKS the
  // mixed-terminal collapse in `uniformReason`, so a selection blocked by one
  // sold lot and one withdrawn lot names the sold lot rather than reciting the
  // generic five-phase terminal list.
  //
  // NOTE (BL-1299): this is the LIFECYCLE axis only. `group` / `ungroup` are
  // non-lifecycle batch changes and are unaffected — a sold lot may join an
  // extension group (matrix §5.5 as amended), it just never has its own close
  // time rewritten.
  | 'sold-in-selection'
  | 'active-lock' // an ACTIVE_LOCKED_FIELDS change on a bid-bearing live lot (BL-1299 removed the group/ungroup clause — those are no longer phase-blocked)
  | 'bid-revert' // reverting/removing a bid-on lot (bids would orphan)
  | 'ceiling' // parent-auction-phase ceiling forbids the target status
  // BL-1043 lifecycle-transition reason (lot-transition-guard.ts): a lot that
  // has not opened for bidding yet (draft/ready) can't be withdrawn. Lives in
  // this shared vocabulary so the transition guard and the batch verdicts speak
  // one language (the two cores converge in BL-1071).
  | 'not-withdrawable'
  // BL-1044 lifecycle-transition reason (lot-transition-guard.ts): a lot whose
  // derived phase can't accept an open transition — already opened / not yet
  // ready / paused for open-for-bidding, or anything but `ready` for
  // open-for-pre-bidding. The lot-phase axis only; the structural openability
  // fields (ring/lot#/close_time) + auction-phase gate stay in the service
  // outcome union. Shared vocabulary with the batch verdicts (converge in BL-1071).
  | 'not-openable'
  // BL-1169 lifecycle-transition reason (lot-transition-guard.ts): a lot that is
  // not closed (any non-terminal phase) — or is soft-deleted — can't be reopened.
  // The inverse of `not-withdrawable`, in the same shared vocabulary.
  | 'not-reopenable'
  // BL-1296 lifecycle-transition reason (lot-transition-guard.ts): the timed
  // close (matrix §3.7) only freezes a lot whose close time has already passed —
  // `awaiting-resolution` or `last-call`. Every other phase is `not-closeable`.
  // Cron-driven, so this reason is rarely operator-facing; it exists so a direct
  // call answers in the shared vocabulary rather than throwing.
  | 'not-closeable'
  // BL-1296 lifecycle-transition reason (lot-transition-guard.ts): matrix §3.6 —
  // a result can only be recorded on a lot that has been open for bidding, so
  // `draft` / `ready` / `pre-bidding` are `not-disposable`. `sold` is ALSO
  // blocked: leaving a sale carries financial teardown and goes through Reverse
  // Sale, not `dispose`. `deleted` is restored first.
  | 'not-disposable'
  // BL-1431 lifecycle-transition reason (lot-transition-guard.ts): matrix §3.9
  // as amended 2026-08-31 — `soft-delete` is fenced out of `sold`, because
  // leaving a sale carries financial teardown that Reverse Sale owns. Distinct
  // from `sold-lock` (the BATCH-axis reason the bulk bar renders): this one is
  // the TRANSITION-axis answer, and it exists so the guard can say "reverse the
  // sale first" rather than a bare "already sold", which states a fact the
  // operator can already see instead of the next action.
  | 'not-deletable'
  // BL-1052 bulk-open ceiling reason: the parent AUCTION isn't in the phase the
  // open action requires (open-for-bidding needs Active + not paused;
  // open-for-pre-bidding needs Ready). Opening never changes auction status, so
  // a wrong-phase auction blocks the action rather than advancing the auction.
  // NOT a guard branch — the guard is lot-phase-axis only (BL-1044); this is a
  // `verdictForBatch` input, computed from the auction the row already carries,
  // exactly like set-status's `ceiling`.
  | 'auction-not-live'
  // BUG-1424 reopen-target reasons. Distinct from `auction-not-live` — and from
  // each other — because each names a DIFFERENT auction-level remedy, and matrix
  // invariant I4a specifies the copy: a disabled row must say "move the auction
  // back to Accepting pre-bids first", never "the auction is past pre-bidding"
  // (a fact the operator can already see, with no next action). One shared
  // reason could only carry one of the two sentences.
  //
  // Produced by `parentAuctionBlockForLandingTarget`, which is a function of the
  // chosen TARGET rather than of the transition — `reopen` is the one transition
  // whose destination the operator picks at call time, so its auction
  // precondition cannot live on the transition definition the way the two open
  // doors' does.
  | 'auction-past-pre-bidding' // → `pre-bidding`, but the auction is not accepting pre-bids (I4a)
  | 'auction-not-opened' // → `open-for-bidding`, but the auction has never opened
  // BUG-1506 (matrix §7p): → `open-for-bidding` under a parent that is `closed`,
  // or whose sale has finished and is awaiting the close sweep (BL-1516's
  // `isAuctionAwaitingClose`, matrix §7w), and whose go-live gate no longer passes. Distinct from
  // `auction-not-opened`, which asks whether the auction EVER opened; this one
  // asks whether it could legally open NOW. Both can be true of different
  // auctions and each names a different remedy — "open the auction" vs "correct
  // its schedule and terms" — so one shared reason could only carry one
  // sentence, exactly as with the BUG-1424 pair above.
  //
  // Scoped to those two cases because they are the only ones where the landing
  // ITSELF re-derives the auction into a bid-accepting phase with no gated
  // auction transition crossed. A RUNNING `active` / `paused` parent already
  // cleared `open`'s gate, and its `first_lot_close_at` is legitimately past the
  // moment its first lot closes — which is why BL-1516 threads the finished-sale
  // predicate in rather than testing the (now identical) phase.
  | 'auction-not-go-live-ready'
  // BUG-1728: a `restore` landing under a parent auction that is itself
  // soft-deleted. Every landing is refused, `draft` and `ready` included — they
  // read no auction column, so before this reason existed they answered "fine"
  // and the lot came back live under an auction nobody can reach. That breaks
  // ADR 018 — Soft Delete's restrict model from the child side: the auction's own
  // delete is refused while live lots exist, so a restore must not create one.
  // Its own reason because its remedy is its own — restore the AUCTION first.
  | 'auction-deleted'
  // BL-2280 (PRJ-89 D7, matrix §7al): a non-terminal landing (reopen, restore,
  // reverse sale to a live target) under a parent auction at a post-close stage.
  // The landing would un-close it (BUG-1226) and silently drop its stage, so the
  // remedy is one level up — move the auction back to plain Closed first.
  | 'auction-close-stage-set'
  // BUG-2150 (rule #23 sign-off, Michael Lawrie, 2026-10-19): reopen or reverse
  // sale on a lot an invoice still lists. Un-selling it would leave the old buyer
  // billed for a lot that is no longer theirs, so the remedy is to take the lot
  // off that invoice first.
  | 'on-invoice'
  // BL-3019 (rule #23 sign-off, Michael Lawrie, 2026-11-04): reopen or reverse
  // sale on a lot a settlement still lists as a sale. The seller-side twin of
  // `on-invoice`; the remedy is to take the lot off that settlement first.
  | 'on-settlement'
  // BUG-2507 (matrix §7ap, Dan Saper 2026-10-31): `set-status` → Upcoming on a lot
  // whose auction is `active` or `paused`. The auto-promote rule
  // (`resolveAutoPromoteOnSave`) turns a `ready` lot straight back into an open one
  // there, so the move could only ever be a silent no-op (or, from Draft, an open
  // the operator never asked for). Its own reason because its remedy is its own:
  // move the lot to Draft instead.
  | 'auction-opened'
  // BL-1293 structural reason: the lot is not in an auction at all
  // (`auction_id IS NULL` — ADR 028's unassociated lot). Every auction-scoped
  // action is unreachable for it by construction, since there is no auction to
  // derive an opening window from. Named as its own cause because the incidental
  // reasons it used to fall through to describe a SYMPTOM rather than the
  // problem: `withdraw` answered `not-withdrawable` (true of any draft/ready
  // lot) and the two opens answered `not-openable` (because a home lot happens
  // to carry no ring / lot number / close time). Both read as "almost eligible"
  // to an operator whose actual next step is "put it in an auction first".
  | 'not-in-auction'
  // BL-817 selection-shape reasons — cross-row invariants that per-lot
  // `canBatchApply` cannot express (computed by `selectionShapeIneligible`):
  | 'group-split' // reassign-ring would split an extension group (not all members selected)
  | 'group-multi-ring' // group requires all selected lots to share one ring
  | 'no-group' // ungroup requires at least one grouped lot in the selection
  // BL-1073 (ADR 034) — the two FIELD-editability reasons the `auctionId` move
  // verdict produces (`LockReason` in `lot-field-editability.ts`). The bulk
  // `move-to-auction` action routes to that evaluator, so the batch and field
  // vocabularies converge on one set of strings rather than the batch side
  // borrowing an approximate reason.
  | 'has-bids' // the lot carries bids / active pre-bids — its bid state can't move auctions
  | 'phase-locked' // the lot is past draft/ready (or terminal) — never movable
  // BL-1289 lifecycle-transition reasons (lot-transition-guard.ts):
  // a lot that is not soft-deleted can't be RESTORED. The `restore` mirror of
  // `not-reopenable` — reopen un-terminals a resolved lot, restore un-deletes a
  // deleted one, and matrix §3.8/§3.10 keep them separate transitions precisely
  // because a deleted lot is restored rather than reopened.
  | 'not-restorable'
  // BUG-2380: the batch check's reverse-sale entry, on a lot that is not sold.
  // Reverse sale is the sold lot's own exit, so every other phase is refused —
  // the same answer the write gives ("Lot is no longer sold").
  | 'not-sold'
  // the lot is ALREADY in the phase the transition targets, so there is nothing
  // to do. Distinct from a hard block: the matrix grid renders these cells as
  // `—` (current phase) rather than ✕, and the operator wants "you're already
  // there", not "that's forbidden". Produced by `mark-draft` from `draft`,
  // `mark-ready` from `ready`, and `soft-delete` from `deleted`.
  | 'already-in-phase'
  // BL-1073: the lot's phase can't be derived (a non-terminal Live Webcast lot —
  // live batch editing is out of scope, BL-16). Previously borrowed `active-lock`,
  // which reads to the operator as "live with bids" — wrong and unactionable.
  | 'unsupported-live'
  // BUG-1420: the VIEWER's role may not take this action on a lot in this PHASE.
  // Today the one producer is `delete`: the operator ruling (2026-08-31) is that
  // a Cataloger deletes `draft`/`ready` lots and nothing past that, while an
  // elevated role (`auctions.manage`) keeps every phase BL-1431 made deletable.
  //
  // The ONLY reason in this vocabulary that is about the viewer rather than the
  // lot, which is why it cannot be expressed by `canBatchApply` (a pure
  // IsomorphicService with no auth in scope, rule #21) and is applied one layer
  // out in admin-web's `verdictForBatch` — the single module BOTH the check
  // service (UI gating) and the edit service (server enforcement) consult. That
  // shared seam is what makes the client and server agree by construction
  // rather than by two hand-maintained gates, which is the bug this closes.
  //
  // Distinct from `phase-locked`, which states that NO role may act on this lot.
  // This one says the phase is fine for someone — just not for you — so its copy
  // must name the role limit, never recite a fact about the lot.
  | 'role-phase'
  // BL-2318: the VIEWER's role may not take this action at all, in any phase
  // (e.g. Accounting and Draft/Ready, Withdraw, Delete). Like `role-phase`, it is
  // about the viewer rather than the lot, so it is never produced here: the batch
  // CHECK overlays it from the one action → permission map the batch write route
  // enforces (`batch-action-permissions.ts`), and only on a row the lot axis
  // already allowed — a lot-axis reason names a remedy and outranks it.
  | 'role-action';

/** The per-lot verdict (matrix §2). A caller running per-item mode collects the
 *  blocked ones as skip reasons; all-or-nothing mode aborts if any is blocked. */
export type BatchApplyVerdict =
  | { allowed: true }
  | { allowed: false; reason: BatchBlockReason };

/**
 * The `set-status` (Draft ↔ Upcoming) arm of {@link canBatchApply}, over the phase KIND alone:
 * terminal outranks the ceiling. Exported so a caller holding only a cached phase kind (the
 * phone's offline check, BL-2437) runs the same rule body as the batch matrix.
 *
 * @param input.lotPhaseKind - The lot's ADR 023 phase kind.
 * @param input.auctionAllowsTargetStatus - The ceiling's answer for the target (`isLotStatusAllowed`).
 * @returns The verdict.
 */
export function canSetLotStatus(input: { lotPhaseKind: LotPhase['kind']; auctionAllowsTargetStatus: boolean }): BatchApplyVerdict {
  if (isLotPhaseTerminal({ kind: input.lotPhaseKind })) return { allowed: false, reason: 'terminal' };
  if (!input.auctionAllowsTargetStatus) return { allowed: false, reason: 'ceiling' };
  return { allowed: true };
}

/**
 * Decide whether a single batch change may be applied to a lot in its current
 * derived phase. The encoding of the signed-off transition-safeguard matrix.
 *
 * @param input.lotPhase The lot's ADR-023 derived phase (from `deriveLotPhase` or
 *   a snapshot `phase`). The predicate reads phase, never stamps.
 * @param input.change The requested batch change.
 * @param input.hasBids Whether the lot has any bid (live, standing max, or
 *   pre-bid). Gates the `delete` arm's `bid-revert` (matrix note 6). NOT read
 *   by `set-status` — BUG-1530 removed that rung — nor by ring moves, which
 *   BL-1927 moved onto the lot rule book.
 * @param input.auctionAllowsTargetStatus For `set-status` only: whether the
 *   parent auction's derived phase permits the target status. The CALLER computes
 *   this via the single ceiling source `isLotStatusAllowed(target, auctionLabel)`
 *   so the predicate never re-implements the ceiling (matrix §2 note 3, AC 4).
 *   Defaults `true` (non-set-status changes ignore it).
 * @returns {@link BatchApplyVerdict}.
 * @sideEffect none — pure.
 */
export function canBatchApply(input: {
  lotPhase: LotPhase;
  change: BatchChange;
  hasBids: boolean;
  auctionAllowsTargetStatus?: boolean;
}): BatchApplyVerdict {
  const { lotPhase, change, hasBids, auctionAllowsTargetStatus = true } = input;
  const { kind } = lotPhase;

  const isSold = kind === 'sold';
  const isDeleted = kind === 'deleted';
  const isTerminal = isLotPhaseTerminal(lotPhase); // sold/unsold/withdrawn/deleted
  const isPreOpen = kind === 'draft' || kind === 'ready';

  const allow: BatchApplyVerdict = { allowed: true };
  const block = (reason: BatchBlockReason): BatchApplyVerdict => ({ allowed: false, reason });

  switch (change.type) {
    // BL-1299 AC 3 (matrix §5.5 as amended, re-signed 2026-08-31): grouping is
    // allowed in EVERY phase but `deleted`. It was pre-open-only, which meant
    // the bulk bar greyed both actions out the moment a lot opened — but linking
    // lots that should close together is a normal mid-sale need, and refusing it
    // left operators with no path at all.
    //
    // The close-time consequence differs by side of the schedule lock, and that
    // asymmetry lives in `batch-lot-edit-service` / `LotCloseTimeService`, NOT
    // here: pre-open, grouping re-derives the stagger (members collapse onto one
    // slot, AC 7); once the auction is live-safe it is a stamped sync to the
    // latest close among the selected members (AC 4/5). This predicate answers
    // "may the operator ask for it", never "what does it do".
    //
    // `sold` is deliberately allowed: AC 5 requires a settled lot to be able to
    // JOIN a group (its own close time is never rewritten). A soft-deleted lot is
    // the one refusal — it is out of the sale, so there is nothing to link.
    //
    // This used to carry a caveat — "allowed even though extensionGroupId ∈
    // SOLD_LOCKED_FIELDS" — reconciling a contradiction that was never real. No
    // route ever accepted `parentLotId` / `linkType` / `extensionGroupId` in a
    // body, so their membership in the sold set was unreachable in the edit gate
    // and constrained nothing here. BL-1308 removed them from that set, and the
    // caveat with them.
    case 'group':
    case 'ungroup':
      return isDeleted ? block('terminal') : allow;

    // Deleting a lot is one write to `auction_lots.deleted_at`. BL-1431 made
    // that one rule (operator sign-off 2026-08-31; matrix §3.9 "Allowed from" +
    // §5.2), collapsing the `remove-from-auction` twin that then meant the same
    // delete and disagreed here. "Remove from auction" now names a move with no
    // destination (BL-1870 / BL-1911, matrix §7ae) — not this delete.
    //
    // Order is load-bearing:
    //   1. `deleted` — nothing to delete twice.
    //   2. `sold` — FENCED, not terminal. Reverse Sale owns the financial
    //      teardown, and the row's copy has to say "reverse the sale first",
    //      which a shared `terminal` reason cannot express. Every OTHER row on a
    //      sold lot already routes to Reverse Sale (M4); this keeps Delete from
    //      being the one exception that quietly hides an invoiced lot.
    //   3. bids — from ANY phase, not just a live one. The old `isLive`
    //      narrowing let a bid-bearing pre-open lot through; the retired
    //      `delete` twin blocked it. Blocking is the safe reading: the bids
    //      would orphan either way.
    //
    // Terminal-but-not-sold (`withdrawn` / `unsold`) is DELETABLE —
    // §5.2's blanket terminal immutability was amended for this axis. A lot that
    // closed without selling owes nothing; if it took bids, rule 3 catches it.
    case 'delete':
      if (isDeleted) return block('terminal');
      if (isSold) return block('sold-lock');
      return hasBids ? block('bid-revert') : allow;

    // Safe targets are draft/ready only (marking sold/active is not a bulk status
    // set — matrix §5.1). Terminal outranks the ceiling.
    //
    // BUG-1530 (re-sign: Michael Lawrie, 2026-09-10 — matrix §2 note 2 amended):
    // this arm used to carry a third rung, `if (isLive) return hasBids ?
    // block('bid-revert') : allow`, refusing to move a bid-bearing live lot back
    // to draft/ready. It is gone, and `hasBids` is no longer read here at all.
    //
    // It guarded a harm that does not occur. lifecycle-transition-matrix.md:48
    // states the invariant — "No lifecycle transition destroys bid state. Placed
    // bids and standing max bids survive every transition" — and `mark-draft`'s
    // write list (matrix §3.1) clears decision stamps and touches no bid row. So
    // the reverted lot keeps its bids and they become live again on reopen;
    // nothing is orphaned. The operator is warned instead, by the caution the
    // `mark-draft` / `mark-ready` transitions declare.
    //
    // Scoped: `terminal` and `ceiling` are untouched, and `delete` below keeps
    // its own (wider) bid block — deleting a lot is not reverting one.
    case 'set-status':
      return canSetLotStatus({ lotPhaseKind: kind, auctionAllowsTargetStatus });

    // Only an unassociated home lot (derives draft/ready) can be added to an
    // auction; anything else is structurally n/a → blocked.
    case 'assign-to-auction':
      if (isPreOpen) return allow;
      if (isTerminal) return block('terminal');
      return block('active-lock');

    // BL-1073 (lifted from the batch adapter, behaviour unchanged): COPY into
    // another auction (ADR 028 §5). A copy takes no bid state with it and leaves
    // the source intact, so every phase qualifies — including `sold`, which is
    // exactly the relist path. Only a deleted source has nothing to copy.
    case 'copy-to-auction':
      return isDeleted ? block('terminal') : allow;
  }
}
